import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore'
import { db } from '../../firebase'
import FactionForm from './FactionForm'
import WrestlerAuction from '../wrestler/WrestlerAuction'
import {
  buildFactionMemberFields,
  getFactionStoredWrestlers,
  mergeFactionWrestlersWithDetails
} from '../../utils/factionWrestlers'
import {
  buildFactionLineupUpdateAfterSwap,
  buildSwapReplacementMap
} from '../../utils/factionLineup'
import {
  getFactionMemberSwapSummary,
  hasFactionLineupAtDate,
  rosterRequiresLineup
} from '../../utils/factionScoring'
import {
  buildSwapRequestPairings,
  getSwapRequestPreviewLabel,
  getSwapRequestWrestlerIds
} from '../../utils/swapRequests'

const buildSwapPayload = (entry, wrestlerInId, wrestlerInScore) => ({
  id: wrestlerInId,
  wrestlerId: wrestlerInId,
  cost: entry.cost || 0,
  startScore: wrestlerInScore || 0,
  accumulatedPoints: 0,
  currentScore: wrestlerInScore || 0,
  status: entry.status || null
})

const buildSwapHistory = (wrestlerOutId, wrestlerInId, swapSummary, slotCost) => ({
  wrestlerOut: doc(db, 'wrestlers', wrestlerOutId),
  wrestlerIn: doc(db, 'wrestlers', wrestlerInId),
  scoreOut: swapSummary.savedPoints,
  currentPointsOut: swapSummary.currentPoints,
  totalPointsOut: swapSummary.totalPoints,
  sumPointsOut: swapSummary.savedPoints,
  priceOut: slotCost || 0,
  priceIn: slotCost || 0,
  date: serverTimestamp(),
  operationType: 'swap'
})

const normalizeGender = value => {
  const normalized = (value ?? '').toString().trim().toLowerCase()

  if (['uomo', 'male', 'm', 'man'].includes(normalized)) return 'Uomo'
  if (['donna', 'female', 'f', 'woman'].includes(normalized)) return 'Donna'

  return (value ?? '').toString().trim()
}

const getMinimumCount = value => {
  const minimum = Number(value ?? 0)
  return Number.isFinite(minimum) ? Math.max(0, minimum) : 0
}

const countMembersByGender = (members = []) =>
  members.reduce(
    (counts, member) => {
      const gender = normalizeGender(member?.gender)

      if (gender === 'Uomo') counts.Uomo += 1
      if (gender === 'Donna') counts.Donna += 1

      return counts
    },
    { Uomo: 0, Donna: 0 }
  )

const buildProjectedMembersAfterSwap = ({
  currentMembers = [],
  outgoingIds = [],
  incomingMembers = []
}) => {
  const outgoingSet = new Set(outgoingIds.filter(Boolean))

  return [
    ...currentMembers.filter(member => !outgoingSet.has(member.id)),
    ...incomingMembers.filter(member => member?.id)
  ]
}

const getGenderLabel = (genderKey, count) => {
  if (genderKey === 'Uomo') {
    return count === 1 ? 'uomo' : 'uomini'
  }

  return count === 1 ? 'donna' : 'donne'
}

const getFactionGenderMinimumViolationAfterSwap = ({
  roster,
  currentMembers = [],
  outgoingIds = [],
  incomingMembers = [],
  messagePrefix = 'Non puoi completare questo scambio',
  subject = 'la fazione'
}) => {
  const minMale = getMinimumCount(roster?.minMaleWrestlers)
  const minFemale = getMinimumCount(roster?.minFemaleWrestlers)

  if (!minMale && !minFemale) {
    return null
  }

  const genderCounts = countMembersByGender(
    buildProjectedMembersAfterSwap({
      currentMembers,
      outgoingIds,
      incomingMembers
    })
  )

  if (genderCounts.Uomo < minMale) {
    return `${messagePrefix}: ${subject} scenderebbe a ${genderCounts.Uomo} ${getGenderLabel(
      'Uomo',
      genderCounts.Uomo
    )}, ma il roster richiede almeno ${minMale} ${getGenderLabel('Uomo', minMale)}.`
  }

  if (genderCounts.Donna < minFemale) {
    return `${messagePrefix}: ${subject} scenderebbe a ${genderCounts.Donna} ${getGenderLabel(
      'Donna',
      genderCounts.Donna
    )}, ma il roster richiede almeno ${minFemale} ${getGenderLabel('Donna', minFemale)}.`
  }

  return null
}

const buildFactionMembers = (factionData, wrestlersById) =>
  mergeFactionWrestlersWithDetails(factionData, wrestlersById)

const ManageRosterPageComp = ({
  roster,
  factions,
  participants,
  wrestlers,
  allUsers,
  onReloadFactions,
  activeSection,
  onPendingRequestsChange
}) => {
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [auctionFaction, setAuctionFaction] = useState(null)
  const [swapRequests, setSwapRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(true)
  const [requestActionId, setRequestActionId] = useState('')
  const [requestMessage, setRequestMessage] = useState('')

  const participantRows = useMemo(
    () =>
      participants.map(participant => {
        const faction = factions.find(item => item.ownerUid === participant.id)
        const wrestlerCount = faction?.wrestlerIds?.length || 0
        const targetCount = Number(roster.maxWrestlers) || 0
        const completion = targetCount
          ? Math.round((wrestlerCount / targetCount) * 100)
          : 0
        const lineupRequired = faction
          ? rosterRequiresLineup(roster, faction)
          : false
        const hasLineup = lineupRequired
          ? hasFactionLineupAtDate(faction)
          : false

        return {
          participant,
          faction,
          wrestlerCount,
          completion,
          lineupRequired,
          hasLineup
        }
      }),
    [factions, participants, roster]
  )

  const wrestlersById = useMemo(
    () =>
      wrestlers.reduce(
        (acc, wrestler) => ({
          ...acc,
          [wrestler.id]: wrestler
        }),
        {}
      ),
    [wrestlers]
  )

  const wrestlerNames = useMemo(
    () =>
      wrestlers.reduce(
        (acc, wrestler) => ({
          ...acc,
          [wrestler.id]: wrestler.name || 'Wrestler sconosciuto'
        }),
        {}
      ),
    [wrestlers]
  )

  const loadSwapRequests = useCallback(async () => {
    setRequestsLoading(true)
    try {
      const snap = await getDocs(
        collection(db, 'rosters', roster.id, 'swapRequest')
      )
      const data = snap.docs
        .map(requestDoc => ({ id: requestDoc.id, ...requestDoc.data() }))
        .sort(
          (a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0)
        )
      setSwapRequests(data)
    } catch (error) {
      console.error('Errore caricamento richieste scambio:', error)
      setRequestMessage('Errore nel caricamento delle richieste di scambio.')
    } finally {
      setRequestsLoading(false)
    }
  }, [roster.id])

  useEffect(() => {
    loadSwapRequests()
  }, [loadSwapRequests])

  useEffect(() => {
    onPendingRequestsChange?.(
      swapRequests.filter(item => item.status === 'pending').length
    )
  }, [onPendingRequestsChange, swapRequests])

  const closeAuction = () => {
    setAuctionFaction(null)
    onReloadFactions?.()
  }

  const handleApproveRequest = async requestItem => {
    setRequestActionId(requestItem.id)
    setRequestMessage('')

    try {
      const requesterWrestlerIds = getSwapRequestWrestlerIds(
        requestItem,
        'requester'
      )
      const targetWrestlerIds = getSwapRequestWrestlerIds(requestItem, 'target')

      if (
        requesterWrestlerIds.length === 0 ||
        targetWrestlerIds.length === 0 ||
        requesterWrestlerIds.length !== targetWrestlerIds.length
      ) {
        throw new Error(
          'La richiesta non e valida: serve lo stesso numero di wrestler per entrambe le fazioni.'
        )
      }

      await runTransaction(db, async transaction => {
        const requestRef = doc(
          db,
          'rosters',
          roster.id,
          'swapRequest',
          requestItem.id
        )
        const requesterFactionRef = doc(
          db,
          'factions',
          requestItem.requesterFactionId
        )
        const targetFactionRef = doc(
          db,
          'factions',
          requestItem.targetFactionId
        )
        const requesterWrestlerRefs = requesterWrestlerIds.map(wrestlerId =>
          doc(db, 'wrestlers', wrestlerId)
        )
        const targetWrestlerRefs = targetWrestlerIds.map(wrestlerId =>
          doc(db, 'wrestlers', wrestlerId)
        )

        const requestSnap = await transaction.get(requestRef)
        const requesterFactionSnap = await transaction.get(requesterFactionRef)
        const targetFactionSnap = await transaction.get(targetFactionRef)
        const requesterWrestlerSnaps = await Promise.all(
          requesterWrestlerRefs.map(requesterWrestlerRef =>
            transaction.get(requesterWrestlerRef)
          )
        )
        const targetWrestlerSnaps = await Promise.all(
          targetWrestlerRefs.map(targetWrestlerRef =>
            transaction.get(targetWrestlerRef)
          )
        )

        if (!requestSnap.exists()) throw new Error('Richiesta non trovata')
        if (requestSnap.data().status !== 'pending')
          throw new Error('La richiesta non e piu pendente')
        if (!requesterFactionSnap.exists() || !targetFactionSnap.exists())
          throw new Error('Una delle fazioni non esiste piu')
        if (
          requesterWrestlerSnaps.some(requesterWrestlerSnap => !requesterWrestlerSnap.exists()) ||
          targetWrestlerSnaps.some(targetWrestlerSnap => !targetWrestlerSnap.exists())
        ) {
          throw new Error('Uno dei wrestler non esiste piu')
        }

        const requesterFaction = {
          id: requesterFactionSnap.id,
          ...requesterFactionSnap.data()
        }
        const targetFaction = {
          id: targetFactionSnap.id,
          ...targetFactionSnap.data()
        }
        const requesterStoredWrestlers = getFactionStoredWrestlers(
          requesterFaction
        )
        const targetStoredWrestlers = getFactionStoredWrestlers(targetFaction)
        const requesterEntriesById = requesterStoredWrestlers.reduce(
          (acc, item) => {
            acc[item.id] = item
            return acc
          },
          {}
        )
        const targetEntriesById = targetStoredWrestlers.reduce(
          (acc, item) => {
            acc[item.id] = item
            return acc
          },
          {}
        )

        if (
          requesterWrestlerIds.some(wrestlerId => !requesterEntriesById[wrestlerId]) ||
          targetWrestlerIds.some(wrestlerId => !targetEntriesById[wrestlerId])
        ) {
          throw new Error(
            'Uno dei wrestler selezionati non appartiene piu alla fazione indicata'
          )
        }

        const liveWrestlersById = { ...wrestlersById }

        requesterWrestlerIds.forEach((wrestlerId, index) => {
          const snapData = requesterWrestlerSnaps[index].data() || {}
          liveWrestlersById[wrestlerId] = {
            ...(liveWrestlersById[wrestlerId] || {}),
            ...snapData,
            id: wrestlerId,
            score:
              snapData.score ??
              liveWrestlersById[wrestlerId]?.score ??
              0
          }
        })
        targetWrestlerIds.forEach((wrestlerId, index) => {
          const snapData = targetWrestlerSnaps[index].data() || {}
          liveWrestlersById[wrestlerId] = {
            ...(liveWrestlersById[wrestlerId] || {}),
            ...snapData,
            id: wrestlerId,
            score:
              snapData.score ??
              liveWrestlersById[wrestlerId]?.score ??
              0
          }
        })

        const requesterMembers = buildFactionMembers(
          requesterFaction,
          liveWrestlersById
        )
        const targetMembers = buildFactionMembers(targetFaction, liveWrestlersById)
        const requesterGenderViolation = getFactionGenderMinimumViolationAfterSwap(
          {
            roster,
            currentMembers: requesterMembers,
            outgoingIds: requesterWrestlerIds,
            incomingMembers: targetWrestlerIds.map(
              wrestlerId => liveWrestlersById[wrestlerId]
            ),
            messagePrefix: 'Questo scambio non e piu valido',
            subject: `la fazione ${requesterFaction.name || 'richiedente'}`
          }
        )
        if (requesterGenderViolation) {
          throw new Error(requesterGenderViolation)
        }

        const targetGenderViolation = getFactionGenderMinimumViolationAfterSwap({
          roster,
          currentMembers: targetMembers,
          outgoingIds: targetWrestlerIds,
          incomingMembers: requesterWrestlerIds.map(
            wrestlerId => liveWrestlersById[wrestlerId]
          ),
          messagePrefix: 'Non puoi approvare questo scambio',
          subject: `la fazione ${targetFaction.name || 'destinataria'}`
        })
        if (targetGenderViolation) {
          throw new Error(targetGenderViolation)
        }

        const requesterReplacementMap = buildSwapReplacementMap(
          requesterWrestlerIds,
          targetWrestlerIds
        )
        const targetReplacementMap = buildSwapReplacementMap(
          targetWrestlerIds,
          requesterWrestlerIds
        )
        const requesterSwapSummariesById = {}
        const targetSwapSummariesById = {}

        requesterWrestlerIds.forEach((wrestlerId, index) => {
          const requesterEntry = requesterEntriesById[wrestlerId]
          const requesterWrestlerScore =
            requesterWrestlerSnaps[index].data().score || 0

          requesterSwapSummariesById[wrestlerId] = getFactionMemberSwapSummary({
            faction: requesterFaction,
            roster,
            wrestlerId,
            currentScore: requesterWrestlerScore,
            startScore: requesterEntry.startScore || 0,
            accumulatedPoints: requesterEntry.accumulatedPoints || 0,
            status: requesterEntry.status || null
          })
        })

        targetWrestlerIds.forEach((wrestlerId, index) => {
          const targetEntry = targetEntriesById[wrestlerId]
          const targetWrestlerScore = targetWrestlerSnaps[index].data().score || 0

          targetSwapSummariesById[wrestlerId] = getFactionMemberSwapSummary({
            faction: targetFaction,
            roster,
            wrestlerId,
            currentScore: targetWrestlerScore,
            startScore: targetEntry.startScore || 0,
            accumulatedPoints: targetEntry.accumulatedPoints || 0,
            status: targetEntry.status || null
          })
        })

        const requesterUpdatedWrestlers = requesterStoredWrestlers.map(item => {
          const wrestlerInId = requesterReplacementMap[item.id]

          if (!wrestlerInId) return item

          return {
            ...item,
            ...buildSwapPayload(
              item,
              wrestlerInId,
              liveWrestlersById[wrestlerInId]?.score || 0
            ),
            id: wrestlerInId,
            wrestlerId: wrestlerInId
          }
        })
        const targetUpdatedWrestlers = targetStoredWrestlers.map(item => {
          const wrestlerInId = targetReplacementMap[item.id]

          if (!wrestlerInId) return item

          return {
            ...item,
            ...buildSwapPayload(
              item,
              wrestlerInId,
              liveWrestlersById[wrestlerInId]?.score || 0
            ),
            id: wrestlerInId,
            wrestlerId: wrestlerInId
          }
        })
        const lineupUpdateRequester = buildFactionLineupUpdateAfterSwap({
          faction: requesterFaction,
          roster,
          updatedWrestlers: requesterUpdatedWrestlers,
          replacements: requesterReplacementMap,
          now: new Date()
        })
        const lineupUpdateTarget = buildFactionLineupUpdateAfterSwap({
          faction: targetFaction,
          roster,
          updatedWrestlers: targetUpdatedWrestlers,
          replacements: targetReplacementMap,
          now: new Date()
        })

        const requesterCurrentPoints = requesterWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (requesterSwapSummariesById[wrestlerId]?.currentPoints || 0),
          0
        )
        const requesterSavedPoints = requesterWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (requesterSwapSummariesById[wrestlerId]?.savedPoints || 0),
          0
        )
        const targetCurrentPoints = targetWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (targetSwapSummariesById[wrestlerId]?.currentPoints || 0),
          0
        )
        const targetSavedPoints = targetWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (targetSwapSummariesById[wrestlerId]?.savedPoints || 0),
          0
        )

        transaction.update(requesterFactionRef, {
          ...buildFactionMemberFields(requesterUpdatedWrestlers),
          ...(lineupUpdateRequester || {}),
          accumulatedPoints:
            (requesterFaction.accumulatedPoints || 0) + requesterCurrentPoints,
          sumPoints:
            (requesterFaction.sumPoints || 0) + requesterSavedPoints,
          updatedAt: serverTimestamp()
        })
        transaction.update(targetFactionRef, {
          ...buildFactionMemberFields(targetUpdatedWrestlers),
          ...(lineupUpdateTarget || {}),
          accumulatedPoints:
            (targetFaction.accumulatedPoints || 0) + targetCurrentPoints,
          sumPoints:
            (targetFaction.sumPoints || 0) + targetSavedPoints,
          updatedAt: serverTimestamp()
        })

        requesterWrestlerIds.forEach((wrestlerId, index) => {
          const requesterEntry = requesterEntriesById[wrestlerId]
          const targetWrestlerId = targetWrestlerIds[index]

          transaction.set(
            doc(
              collection(db, 'factions', requestItem.requesterFactionId, 'swaps')
            ),
            buildSwapHistory(
              wrestlerId,
              targetWrestlerId,
              requesterSwapSummariesById[wrestlerId],
              requesterEntry.cost || 0
            )
          )
        })
        targetWrestlerIds.forEach((wrestlerId, index) => {
          const targetEntry = targetEntriesById[wrestlerId]
          const requesterWrestlerId = requesterWrestlerIds[index]

          transaction.set(
            doc(collection(db, 'factions', requestItem.targetFactionId, 'swaps')),
            buildSwapHistory(
              wrestlerId,
              requesterWrestlerId,
              targetSwapSummariesById[wrestlerId],
              targetEntry.cost || 0
            )
          )
        })

        transaction.update(requestRef, {
          status: 'accepted',
          approvedAt: serverTimestamp(),
          approvedBy: roster.ownerUid
        })
      })

      setRequestMessage(
        'Richiesta approvata e scambio completato con successo.'
      )
      await Promise.all([loadSwapRequests(), onReloadFactions?.()])
    } catch (error) {
      console.error('Errore approvazione richiesta:', error)
      setRequestMessage(
        error.message || 'Errore durante l approvazione della richiesta.'
      )
    } finally {
      setRequestActionId('')
    }
  }

  const handleRejectRequest = async requestItem => {
    setRequestActionId(requestItem.id)
    setRequestMessage('')
    try {
      await updateDoc(
        doc(db, 'rosters', roster.id, 'swapRequest', requestItem.id),
        {
          status: 'rejected',
          rejectedAt: serverTimestamp(),
          rejectedBy: roster.ownerUid
        }
      )
      setRequestMessage('Richiesta rifiutata.')
      await loadSwapRequests()
    } catch (error) {
      console.error('Errore rifiuto richiesta:', error)
      setRequestMessage('Errore durante il rifiuto della richiesta.')
    } finally {
      setRequestActionId('')
    }
  }

  const pendingRequests = swapRequests.filter(item => item.status === 'pending')

  return (
    <>
      {activeSection === 'participants' && (
        <section className='manage-roster-section'>
          <div className='manage-roster-section__header'>
            <div>
              <h2>Partecipanti e fazioni</h2>
            </div>
          </div>

          <div className='manage-roster-grid'>
            {participantRows.map(
              ({
                participant,
                faction,
                wrestlerCount,
                completion,
                lineupRequired,
                hasLineup
              }) => (
                <article
                  className='manage-roster-player-card'
                  key={participant.id}
                >
                  <div className='manage-roster-player-card__head'>
                    <div>
                      <h3>{participant.username || participant.email}</h3>
                      <p>{participant.email}</p>
                    </div>
                    {faction && lineupRequired && (
                      <span
                        className={`manage-roster-player-card__badge ${
                          hasLineup ? 'is-ready' : 'is-missing'
                        }`}
                      >
                        <i
                          className={`bi ${
                            hasLineup
                              ? 'bi-check-circle-fill'
                              : 'bi-exclamation-circle-fill'
                          }`}
                        />
                        {hasLineup ? 'Line-up ok' : 'Line-up assente'}
                      </span>
                    )}
                  </div>

                  {faction ? (
                    <>
                      <div className='text-light'>
                        <div>
                          <small>Fazione</small>
                          <strong>{faction.name}</strong>
                        </div>
                        <div>
                          <small>Crediti residui</small>
                          <strong>{faction.creditsLeft || 0}</strong>
                        </div>
                      </div>

                      <div className='manage-roster-player-card__progress'>
                        <div className='d-flex justify-content-between'>
                          <small>Fazione completata</small>
                          <small>
                            {wrestlerCount}/{roster.maxWrestlers}
                          </small>
                        </div>
                        <div className='progress'>
                          <div
                            className={`progress-bar ${
                              completion === 100
                                ? 'bg-success'
                                : 'bg-brand-secondary'
                            }`}
                            role='progressbar'
                            style={{ width: `${completion}%` }}
                          />
                        </div>
                      </div>

                      <div className='manage-roster-player-card__actions'>
                        <button
                          className='btn btn-outline-brand-primary-light'
                          onClick={() => setSelectedParticipant(participant)}
                        >
                          <i className='bi bi-pencil me-2' />
                          Modifica fazione
                        </button>
                        <button
                          className='btn btn-brand-money'
                          onClick={() => setAuctionFaction(faction)}
                        >
                          <i className='bi bi-shop me-2' />
                          Mercato
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className='btn btn-brand-primary w-100'
                      onClick={() => setSelectedParticipant(participant)}
                    >
                      <i className='bi bi-plus-circle me-2' />
                      Crea fazione
                    </button>
                  )}
                </article>
              )
            )}
          </div>
        </section>
      )}

      {activeSection === 'requests' && (
        <section className='manage-roster-section'>
          <div className='manage-roster-section__header'>
            <div>
              <h2>Richieste di scambio</h2>
            </div>
            <span className='badge bg-brand-secondary text-dark'>
              {pendingRequests.length} in attesa
            </span>
          </div>

          {requestMessage && (
            <div
              className={`alert ${
                requestMessage.toLowerCase().includes('errore')
                  ? 'alert-danger'
                  : 'alert-success'
              }`}
            >
              {requestMessage}
            </div>
          )}

          {requestsLoading ? (
            <div className='card brand-card border-0 shadow-sm'>
              <div className='card-body text-center py-4'>
                Caricamento richieste di scambio...
              </div>
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className='card brand-card border-0 shadow-sm'>
              <div className='card-body text-center py-4 text-brand-muted'>
                Nessuna richiesta di scambio in attesa per questo roster.
              </div>
            </div>
          ) : (
            <div className='manage-roster-grid'>
              {pendingRequests.map(requestItem => {
                const requestPairings = buildSwapRequestPairings(
                  requestItem,
                  wrestlerNames
                )
                const pairCount =
                  requestPairings.filter(
                    pair => pair.requesterId && pair.targetId
                  ).length || requestPairings.length

                return (
                  <article
                    key={requestItem.id}
                    className='manage-roster-player-card'
                  >
                    <div className='manage-roster-player-card__head'>
                      <div>
                        <h3>{requestItem.requesterFactionName}</h3>
                        <p>chiede a {requestItem.targetFactionName}</p>
                      </div>
                      {pairCount > 1 && (
                        <span className='badge rounded-pill bg-brand-secondary text-dark'>
                          {pairCount} wrestler
                        </span>
                      )}
                    </div>

                    <div
                      className='manage-roster-player-card__meta'
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem'
                      }}
                    >
                      <div>
                        <small className='text-muted-brand d-block'>
                          {pairCount > 1
                            ? 'Wrestler proposti'
                            : 'Wrestler proposto'}
                        </small>
                        <strong className='text-light'>
                          {getSwapRequestPreviewLabel(
                            requestItem,
                            'requester',
                            wrestlerNames
                          )}
                        </strong>
                      </div>

                      <div>
                        <small className='text-muted-brand d-block'>
                          {pairCount > 1
                            ? 'Wrestler richiesti'
                            : 'Wrestler richiesto'}
                        </small>
                        <strong className='text-light'>
                          {getSwapRequestPreviewLabel(
                            requestItem,
                            'target',
                            wrestlerNames
                          )}
                        </strong>
                      </div>

                      <div style={{ gridColumn: 'span 2' }}>
                        <small className='text-muted-brand d-block'>Da</small>
                        <strong className='text-light'>
                          {allUsers.find(
                            user => user.id === requestItem.requesterOwnerUid
                          )?.username || 'Utente'}
                        </strong>
                      </div>
                    </div>

                    {pairCount > 1 && (
                      <div className='market-request-summary__pairings mt-3'>
                        <div className='market-request-pairs'>
                          {requestPairings.map((pair, index) => (
                            <div
                              key={`${pair.requesterId || 'none'}-${pair.targetId || 'none'}-${index}`}
                              className='market-request-pair'
                            >
                              <span className='market-request-pair__index'>
                                {index + 1}
                              </span>
                              <div className='market-request-pair__copy'>
                                <strong>{pair.requesterName}</strong>
                                <small>
                                  <i className='bi bi-arrow-left-right me-1' />
                                  {pair.targetName}
                                </small>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className='manage-roster-player-card__actions'>
                      <button
                        className='btn btn-brand-secondary'
                        onClick={() => handleApproveRequest(requestItem)}
                        disabled={requestActionId === requestItem.id}
                      >
                        {requestActionId === requestItem.id ? (
                          <>
                            <span className='spinner-border spinner-border-sm me-2' />
                            Approvo...
                          </>
                        ) : (
                          <>
                            <i className='bi bi-check2-circle me-2' />
                            Approva
                          </>
                        )}
                      </button>
                      <button
                        className='btn btn-outline-danger'
                        onClick={() => handleRejectRequest(requestItem)}
                        disabled={requestActionId === requestItem.id}
                      >
                        <i className='bi bi-x-circle me-2' />
                        Rifiuta
                      </button>
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </section>
      )}

      {selectedParticipant && (
        <div className='manage-roster-modal-backdrop'>
          <div className='manage-roster-modal manage-roster-modal--wide manage-roster-modal--form'>
            <div className='manage-roster-modal__header'>
              <div>
                <h2 className='manage-roster-panel__title'>
                  {factions.find(
                    factionItem =>
                      factionItem.ownerUid === selectedParticipant.id
                  )
                    ? `Fazione di ${
                        selectedParticipant.username ||
                        selectedParticipant.email
                      }`
                    : `Crea fazione per ${
                        selectedParticipant.username ||
                        selectedParticipant.email
                      }`}
                </h2>
              </div>
              <button
                type='button'
                className='btn-close btn-close-white'
                onClick={() => setSelectedParticipant(null)}
              />
            </div>

            <div className='manage-roster-modal__body'>
              <FactionForm
                initialOwnerId={selectedParticipant.id}
                rosterId={roster.id}
                initialRosterName={roster.name}
                roster={roster}
                maxWrestlers={roster.maxWrestlers}
                initialCredits={roster.maxCredits}
                minMaleWrestlers={roster.minMaleWrestlers || 0}
                minFemaleWrestlers={roster.minFemaleWrestlers || 0}
                onlyMainRoster={Boolean(roster.onlyMainRoster)}
                onlyNXT={Boolean(roster.onlyNXT)}
                existingFaction={factions.find(
                  factionItem => factionItem.ownerUid === selectedParticipant.id
                )}
                onSuccess={() => {
                  onReloadFactions?.()
                  setSelectedParticipant(null)
                }}
                onCancel={() => setSelectedParticipant(null)}
                allUsers={allUsers}
                wrestlers={wrestlers}
                factions={factions}
              />
            </div>
          </div>
        </div>
      )}

      {auctionFaction && (
        <div className='manage-roster-modal-backdrop'>
          <div className='manage-roster-modal manage-roster-modal--wide manage-roster-modal--form'>
            <div className='manage-roster-modal__header'>
              <div>
                <span className='manage-roster-panel__eyebrow'>Mercato</span>
                <h2 className='manage-roster-panel__title'>
                  {auctionFaction.name}
                </h2>
              </div>
              <button
                type='button'
                className='btn-close btn-close-white'
                onClick={closeAuction}
              />
            </div>

            <div className='manage-roster-modal__body'>
              <WrestlerAuction
                factionId={auctionFaction.id}
                onAuctionUpdate={onReloadFactions}
              />
            </div>

            <div className='manage-roster-modal__footer'>
              <button
                type='button'
                className='btn btn-outline-light'
                onClick={closeAuction}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ManageRosterPageComp
